import {
    parseISO,
    isValid,
    isAfter,
    isBefore,
    format,
    addHours,
    differenceInMilliseconds,
    isWithinInterval,
} from 'date-fns';
import { de } from 'date-fns/locale';
import axios from 'axios';
import Vue from 'vue';

const maintenanceApi = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    timeout: 10000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

maintenanceApi.interceptors.request.use((config) => {
    const authToken = localStorage.getItem('authToken');
    if (authToken && authToken !== 'token') {
        config.headers.Authorization = `Bearer ${authToken}`;
    }
    return config;
});

class MaintenanceService {
    constructor() {
        this.maintenanceWindow = null;
        this.isInMaintenance = false;
        this.maintenanceMessage = 'TaxiFusion wird bald gewartet.';
        this.maintenanceMessageActive =
            'TaxiFusion wird derzeit gewartet, um Ihnen einen noch besseren Service zu bieten.';
        this.lastUpdated = Date.now();
        this.intervalId = null;
    }

    setMaintenanceWindow(fromHeader, toHeader) {
        if (!fromHeader || !toHeader) {
            this.clearMaintenanceWindow();
            return;
        }

        try {
            const from = parseISO(fromHeader);
            const to = parseISO(toHeader);

            if (!isValid(from) || !isValid(to)) {
                this.clearMaintenanceWindow();
                return;
            }

            this.maintenanceWindow = {
                from,
                to,
                fromHeader,
                toHeader,
            };

            this.lastUpdated = Date.now(); // Update reactive timestamp
            this.checkMaintenanceStatus();

            // Reschedule next check based on new maintenance window
            if (this.intervalId) {
                this.scheduleNextCheck();
            }
        } catch (error) {
            this.clearMaintenanceWindow();
        }
    }

    checkMaintenanceStatus() {
        if (!this.maintenanceWindow) {
            this.isInMaintenance = false;
            return false;
        }

        const now = new Date();
        const isInWindow = isWithinInterval(now, {
            start: this.maintenanceWindow?.from,
            end: this.maintenanceWindow?.to,
        });

        if (this.isInMaintenance !== isInWindow) {
            this.isInMaintenance = isInWindow;
            if (isInWindow) {
                this.onMaintenanceStart();
            } else {
                this.onMaintenanceEnd();
            }
        }

        return this.isInMaintenance;
    }

    clearMaintenanceWindow() {
        const wasInMaintenance = this.isInMaintenance;

        this.maintenanceWindow = null;
        this.isInMaintenance = false;
        this.lastUpdated = Date.now(); // Update reactive timestamp
        this.hideMaintenanceNotification();

        if (wasInMaintenance && typeof window !== 'undefined') {
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        }
    }

    getMaintenanceStatus() {
        const currentMessage = this.isInMaintenance
            ? this.maintenanceMessageActive
            : this.maintenanceMessage;

        return {
            isInMaintenance: this.isInMaintenance,
            maintenanceWindow: this.maintenanceWindow,
            message: currentMessage,
            scheduledDate: this.getMaintenanceScheduledDate(),
        };
    }

    getMaintenanceScheduledDate() {
        return {
            startDate: this.maintenanceWindow?.from,
            endDate: this.maintenanceWindow?.to,
        };
    }

    getTimeUntilMaintenance() {
        if (!this.maintenanceWindow || this.isInMaintenance) {
            return null;
        }

        const now = new Date();
        if (isBefore(now, this.maintenanceWindow?.from)) {
            return differenceInMilliseconds(this.maintenanceWindow?.from, now);
        }

        return null;
    }

    getTimeUntilMaintenanceEnd() {
        if (!this.maintenanceWindow || !this.isInMaintenance) {
            return null;
        }

        const now = new Date();
        return differenceInMilliseconds(this.maintenanceWindow.to, now);
    }

    onMaintenanceStart() {
        this.showMaintenanceNotification();
    }

    onMaintenanceEnd() {
        this.hideMaintenanceNotification();
    }

    showMaintenanceNotification() {
        if (typeof window !== 'undefined') {
            this.createMaintenanceBanner();
        }
    }

    hideMaintenanceNotification() {
        if (typeof window !== 'undefined') {
            this.removeMaintenanceBanner();
        }
    }

    createMaintenanceBanner() {
        this.removeMaintenanceBanner();

        if (!this.maintenanceWindow?.to) {
            return;
        }

        const banner = document.createElement('div');
        banner.id = 'maintenance-banner';
        banner.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            background: #953701;
            color: white;
            padding: 12px;
            text-align: center;
            z-index: 9999;
            font-weight: bold;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        `;

        const currentMessage = this.isInMaintenance
            ? this.maintenanceMessageActive
            : this.maintenanceMessage;
        const endTime = format(this.maintenanceWindow.to, 'HH:mm', { locale: de });

        banner.innerHTML = `
            ${currentMessage}
            <span style="font-size: 0.9em; opacity: 0.9;">
                (Erwartetes Ende: ${endTime})
            </span>
        `;

        document.body.appendChild(banner);
    }

    removeMaintenanceBanner() {
        const existingBanner = document.getElementById('maintenance-banner');
        if (existingBanner) {
            existingBanner.remove();
        }
    }

    startPeriodicCheck(intervalMs = 30000) {
        this.checkMaintenanceStatus();
        this.scheduleNextCheck();
    }

    scheduleNextCheck() {
        if (this.intervalId) {
            clearTimeout(this.intervalId);
        }

        const now = new Date();
        let nextCheckDelay = 3600000;

        if (this.maintenanceWindow) {
            const timeUntilMaintenance = this.getTimeUntilMaintenance();

            if (timeUntilMaintenance !== null) {
                const minutesUntilMaintenance = Math.floor(timeUntilMaintenance / (1000 * 60));
                if (minutesUntilMaintenance <= 15) {
                    nextCheckDelay = 30000;
                } else if (minutesUntilMaintenance <= 60) {
                    nextCheckDelay = 120000;
                } else if (minutesUntilMaintenance <= 1440) {
                    nextCheckDelay = 300000;
                } else {
                    nextCheckDelay = 900000;
                }
            } else if (this.isInMaintenance) {
                nextCheckDelay = 30000;
            } else {
                nextCheckDelay = 3600000;
            }
        }

        this.intervalId = setTimeout(() => {
            this.checkMaintenanceStatus();
            this.checkServerMaintenanceStatus();
            this.scheduleNextCheck();
        }, nextCheckDelay);
    }

    async checkServerMaintenanceStatus() {
        try {
            const baseUrl = import.meta.env.VITE_API_BASE_URL;
            if (!baseUrl) return;

            const response = await maintenanceApi.get('/cpanel/profile/simple', {
                withCredentials: true,
            });

            const maintenanceFrom =
                response.headers['x-maintenance-from'] || response.headers['X-Maintenance-From'];
            const maintenanceTo =
                response.headers['x-maintenance-to'] || response.headers['X-Maintenance-To'];

            if (maintenanceFrom || maintenanceTo) {
                this.setMaintenanceWindow(maintenanceFrom, maintenanceTo);
            } else {
                this.clearMaintenanceWindow();
            }
        } catch (error) {
            if (error.response?.status === 503) {
                const maintenanceFrom =
                    error.response?.headers?.['x-maintenance-from'] ||
                    error.response?.headers?.['X-Maintenance-From'];
                const maintenanceTo =
                    error.response?.headers?.['x-maintenance-to'] ||
                    error.response?.headers?.['X-Maintenance-To'];

                if (maintenanceFrom || maintenanceTo) {
                    this.setMaintenanceWindow(maintenanceFrom, maintenanceTo);
                } else {
                    const now = new Date();
                    const defaultEnd = addHours(now, 2);

                    this.setMaintenanceWindow(now.toISOString(), defaultEnd.toISOString());
                }
            }
        }
    }

    // Optimized method for route change checks - doesn't reschedule periodic checks
    async checkMaintenanceStatusOnRouteChange() {
        try {
            const baseUrl = import.meta.env.VITE_API_BASE_URL;
            if (!baseUrl) return;

            const response = await maintenanceApi.get('/cpanel/profile/simple', {
                withCredentials: true,
            });

            const maintenanceFrom =
                response.headers['x-maintenance-from'] || response.headers['X-Maintenance-From'];
            const maintenanceTo =
                response.headers['x-maintenance-to'] || response.headers['X-Maintenance-To'];

            if (maintenanceFrom || maintenanceTo) {
                // Only update if the maintenance window has changed
                const newFrom = parseISO(maintenanceFrom);
                const newTo = parseISO(maintenanceTo);

                if (
                    !this.maintenanceWindow ||
                    !isValid(this.maintenanceWindow?.from) ||
                    !isValid(this.maintenanceWindow?.to) ||
                    newFrom.getTime() !== this.maintenanceWindow?.from?.getTime() ||
                    newTo.getTime() !== this.maintenanceWindow?.to?.getTime()
                ) {
                    this.setMaintenanceWindow(maintenanceFrom, maintenanceTo);
                }
            } else if (this.maintenanceWindow) {
                // Clear maintenance window if it was set but no longer exists
                this.clearMaintenanceWindow();
            }
        } catch (error) {
            if (error.response?.status === 503) {
                const maintenanceFrom =
                    error.response?.headers?.['x-maintenance-from'] ||
                    error.response?.headers?.['X-Maintenance-From'];
                const maintenanceTo =
                    error.response?.headers?.['x-maintenance-to'] ||
                    error.response?.headers?.['X-Maintenance-To'];

                if (maintenanceFrom || maintenanceTo) {
                    this.setMaintenanceWindow(maintenanceFrom, maintenanceTo);
                } else {
                    const now = new Date();
                    const defaultEnd = addHours(now, 2);

                    this.setMaintenanceWindow(now.toISOString(), defaultEnd.toISOString());
                }
            }
        }
    }

    async checkMaintenanceFinished() {
        try {
            const baseUrl = import.meta.env.VITE_API_BASE_URL;
            if (!baseUrl) return false;

            const response = await maintenanceApi.get('/cpanel/profile/simple', {
                withCredentials: true,
            });

            const maintenanceFrom =
                response.headers['x-maintenance-from'] || response.headers['X-Maintenance-From'];
            const maintenanceTo =
                response.headers['x-maintenance-to'] || response.headers['X-Maintenance-To'];

            if (!maintenanceFrom && !maintenanceTo) {
                this.clearMaintenanceWindow();
                return true;
            }

            return false;
        } catch (error) {
            if (error.response?.status === 503) {
                const maintenanceFrom =
                    error.response?.headers?.['x-maintenance-from'] ||
                    error.response?.headers?.['X-Maintenance-From'];
                const maintenanceTo =
                    error.response?.headers?.['x-maintenance-to'] ||
                    error.response?.headers?.['X-Maintenance-To'];

                if (maintenanceFrom || maintenanceTo) {
                    this.setMaintenanceWindow(maintenanceFrom, maintenanceTo);
                }

                return false;
            }

            return false;
        }
    }

    stopPeriodicCheck() {
        if (this.intervalId) {
            clearTimeout(this.intervalId);
            this.intervalId = null;
        }
    }

    destroy() {
        this.stopPeriodicCheck();
        this.hideMaintenanceNotification();
        this.clearMaintenanceWindow();
    }
}

// Create and export reactive service instance
const maintenanceService = Vue.observable(new MaintenanceService());
export default maintenanceService;
